import { useTranslation } from 'react-i18next';
import TemplatePicker from './TemplatePicker';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTripWizardMode } from '@koob/ui/src/features/tripWizard';
import {
  DesignTripModal,
  useTripStore,
  useTripFilters,
  useSetTripFilters,
} from '@koob/trip-designer';
import { useHydrateTrip } from '../Compose/useHydrateTrip';
import { useAsync } from 'react-async';
import { getFolder } from 'api/folders';
import {
  folderRoomsComposition,
  generateTravelerComposition,
} from '@koob/utils';
import { useActiveFolder } from 'store';

// Binds the shared design-trip modal to TO: the client folder it is opened for
// (its travellers seed the party), the template pickers and the edit routes.
export default function DesignTripFolderModal({ open, close, onExit }) {
  const { t } = useTranslation('tripDesigner');
  const tripFilters = useTripFilters();
  const setTripFilters = useSetTripFilters();
  const days = useTripStore(state => state.days);
  const hydrateTrip = useHydrateTrip();
  const { state, search, pathname } = useLocation();
  const { fromId } = useParams();
  const wizardMode = useTripWizardMode();
  const activeFolder = useActiveFolder();

  const pathParts = pathname.split('/folders/');
  // The Design Trip modal can be opened from anywhere via the navbar button
  // (gated only by an active folder in the global store, see US-014), not
  // just from a /folders/:id route. Fall back to the globally active folder
  // so its travelers still get fetched and used for the default composition.
  const folderId =
    pathParts.length > 1 ? pathParts[1].split('/')[0] : activeFolder?.id;

  const {
    data: folderData,
    error: folderError,
    isPending: folderPending,
  } = useAsync(
    folderId
      ? {
          promiseFn: getFolder,
          id: folderId,
          watch: folderId,
        }
      : {},
  );

  // react-async keeps the previous folder's data while the next one loads.
  const folder =
    folderId && String(folderData?.data?.id) === String(folderId)
      ? folderData.data
      : null;

  // Seed pax/birthdates straight from the freshly-fetched folder — never via
  // tripFilters.composition, which the provider pre-binds and localStorage
  // caches stale (both would hide this folder's real travelers).

  const rawFolderComposition = folder
    ? {
        ...generateTravelerComposition(folder),
        roomsComposition: folderRoomsComposition(folder).map(
          ({ adults, children }) => ({ adults, children }),
        ),
      }
    : null;
  const folderComposition = rawFolderComposition
    ? {
        ...rawFolderComposition,
        childrenBirthdates: rawFolderComposition.childrenBirthdates?.map(
          dateString => new Date(dateString).toISOString(),
        ),
      }
    : null;

  const from = new URLSearchParams(search).get('from') ?? null;
  // Edit mode is signalled two ways: the /design-trip?from=draftEdit route, or
  // openTripWizard('edit') when the draft edit opens the modal in place.
  const update =
    wizardMode === 'edit' ||
    state?.from === 'draftEdit' ||
    from === 'draftEdit';

  // Picking a template loads it into the designer right here — the wizard
  // remounts on the new tripId (see key below) and opens prefilled.
  const handleTemplateSelected = trip => {
    if (folder && !tripFilters?.clientFolder) {
      setTripFilters(current => ({
        ...current,
        clientFolder: folder,
        composition: folderComposition,
      }));
    }
    // Picked template carries its own stored pax and no folder of ours; when
    // a real client folder is bound here, both its composition and its
    // identity must win
    return hydrateTrip(trip.id, {
      folderComposition,
      preferredClientFolder: folder,
    });
  };

  useEffect(() => {
    if (open && folder && !tripFilters?.clientFolder) {
      setTripFilters(current => ({
        ...current,
        clientFolder: folder,
        // Pull pax/birthdates from the folder only when creating; an edited
        // trip keeps its own composition.
        ...(update ? {} : { composition: folderComposition }),
      }));
    }
  }, [open, folder, update]);

  // The wizard reads its seed once on mount, so the folder must be fetched
  // before it renders. Don't block forever if the fetch failed.
  if (open && folderId && !folder && (folderPending || !folderError)) {
    return null;
  }

  // Straight to the flow (no chooser) when resuming a draft or template. A
  // client folder only preselects the client — it still opens on the intro.
  const skipChooser = days.length > 0 || !!fromId;

  const entryChoices = [
    {
      id: 'scratch',
      label: t('designTripModal.fromScratch'),
      icon: <i className="fa-solid fa-wand-magic-sparkles" />,
    },
    {
      id: 'own',
      label: t('designTripModal.fromMyOwn'),
      icon: <i className="fa-solid fa-folder-open" />,
      panel: () => <TemplatePicker onSelect={handleTemplateSelected} />,
    },
    {
      id: 'dmc',
      label: t('designTripModal.fromDmcTemplate'),
      icon: <i className="fa-solid fa-map-location-dot" />,
      panel: () => (
        <TemplatePicker isDistributedTrip onSelect={handleTemplateSelected} />
      ),
    },
  ];

  return (
    <DesignTripModal
      open={open}
      close={close}
      onExit={onExit}
      update={update}
      skipChooser={skipChooser}
      entryChoices={entryChoices}
      editId={fromId}
      seedComposition={folderComposition}
    />
  );
}
